// @ts-nocheck

import * as R from 'ramda';
import { ADS_COHORTS_NAMESPACE, EMAIL_COHORTS_NAMESPACE, JOURNEYS_COHORTS_NAMESPACE } from '../../../../shared/constants';
import { LOADING_STATUS } from '../../main/support/constants';
import {
  DELETE_BOOKMARK,
  GET_BOOKMARKS,
  MOUNT,
  SAVE_BOOKMARK,
  SHAREABLE,
  UPDATE_BOOKMARK,
  setLocalStorage,
} from '../actions/ShareableActions';
import { COHORT_CALCULATED_METRICS } from '../support/eo_mapping/calculated_properties';
import { reducerWrapper } from '../support/utils';
import { convertShareableStateFiltersFromLegacyToNative } from '../support/utils/druid/nativeQueries';
import type { ShareableActionPayload, ShareableReducer, SingleNamespaceData } from '../types/shareableTypes';

/**
 * method that takes legacy 'event' param in shareable state and converts to an array of 'events'
 */
export const convertShareableStateEventToEvents = (shareableState: Record<string, any>, nestingLevel: number) => {
  if (!shareableState) {
    return;
  }

  if (!nestingLevel) {
    return shareableState;
  }

  if (shareableState.event && !(shareableState.events && shareableState.events.length)) {
    return R.assoc('events', [shareableState.event], shareableState);
  }

  return Object.keys(shareableState).reduce((acc, key) => {
    if (R.type(shareableState[key]) !== 'Object') {
      return R.assoc(key, shareableState[key], acc);
    }

    return R.assoc(key, convertShareableStateEventToEvents(shareableState[key], nestingLevel - 1), acc);
  }, {});
};

/**
 * method that takes legacy cohort reports and converts to a multi measure compliant report. This entails the following:
 * 1. cohort_event_type (string) -> cohort_event_types (Array<string>)
 * 2. add any new calculations associated with cohort_event_type to calculations
 * 3. move costPerEvent, cumulative, and perUser to sibling query parameter as cost_per_event, cumulative, and per_user
 */
export const convertLegacyCohortToMultiMeasureCohortReport = (
  shareableState: SingleNamespaceData,
  nestingLevel: number,
  namespace: string
) => {
  if (!shareableState) {
    return;
  }

  if (
    namespace !== ADS_COHORTS_NAMESPACE &&
    namespace !== EMAIL_COHORTS_NAMESPACE &&
    namespace !== JOURNEYS_COHORTS_NAMESPACE
  ) {
    return shareableState;
  }

  if (!nestingLevel) {
    return shareableState;
  }

  // move transformations into query
  if (shareableState.query) {
    if (shareableState.query.per_user === undefined) {
      shareableState.query.per_user = !!shareableState.perUser;
    }

    if (shareableState.query.cost_per_event === undefined) {
      shareableState.query.cost_per_event = !!shareableState.costPerEvent;
    }

    if (shareableState.query.cumulative === undefined) {
      shareableState.query.cumulative = !!shareableState.cumulative;
    }
  }

  if (shareableState.cohort_event_type && !(shareableState.cohort_event_types && shareableState.cohort_event_types.length)) {
    // convert cohort_event_type to cohort_event_types
    shareableState.cohort_event_types = [shareableState.cohort_event_type];

    if (COHORT_CALCULATED_METRICS[shareableState.cohort_event_type]) {
      // add any calculations that correspond to the cohort event type
      shareableState.calculations = [COHORT_CALCULATED_METRICS[shareableState.cohort_event_type]];
    }

    // delete so we don't send these query parameters to druid
    delete shareableState.cohort_event_type;
    delete shareableState.calculation;
  }

  return Object.keys(shareableState).reduce((acc, key) => {
    if (R.type(shareableState[key]) !== 'Object') {
      return R.assoc(key, shareableState[key], acc);
    }

    return R.assoc(
      key,
      convertLegacyCohortToMultiMeasureCohortReport(shareableState[key], nestingLevel - 1, namespace),
      acc
    );
  }, {});
};

const handleLegacyReports = (shareableState: SingleNamespaceData, namespace: string) => {
  const filtersHandled = convertShareableStateFiltersFromLegacyToNative(shareableState, 2);
  const eventHandled = convertShareableStateEventToEvents(filtersHandled, 2) || {};
  const cohortHandled = convertLegacyCohortToMultiMeasureCohortReport(eventHandled, 3, namespace);
  return cohortHandled;
};

export var INIT_STATE: ShareableReducer = {
  data: {},
  getPageState: function (namespace) {
    return handleLegacyReports(this.data[namespace], namespace) || {};
  },
  link: null,
  error: null,
  bookmarked_reports: [],
  loading: false,
  areBookmarkReportsLoaded: false,
};

function Handler(state: ShareableReducer = INIT_STATE, action: ShareableActionPayload) {
  switch (action.type) {
    /**
     * populates shareable state from localStorage on page load
     */
    case MOUNT.response:
      return R.merge(state, {
        data: action.data,
      });

    /**
     * sets localStorage and sets shareable state if user came from a branch link
     */
    case SHAREABLE.from_link:
      const shareable = action.data;

      if (shareable.namespace) {
        setLocalStorage(shareable.namespace, shareable.data);
      }

      return R.merge(state, {
        data: R.assoc(shareable.namespace, shareable.data, state.data),
      });

    case SHAREABLE.set:
      const prevPageState = state.getPageState(action.namespace);
      const newPageState = R.merge(prevPageState, action.data);
      // check for safari private browsing mode
      setLocalStorage(action.namespace, newPageState);
      return R.merge(state, {
        data: R.assoc(action.namespace, newPageState, state.data),
      });

    case SHAREABLE.reset:
      setLocalStorage(action.namespace, {});
      // TODO some how we need to reset to init state, idk how to do that so reload for now!
      const { pathname, search, hash } = window.location;
      setTimeout(() => {
        // resets the location.state from routed components
        // TODO: Router V5 -> Refactoring to "useHistory()"
        window.location = pathname + search + hash;
        // skips caching: https://stackoverflow.com/questions/2405117/difference-between-window-location-href-window-location-href-and-window-location
        window.location.reload(true);
      }, 200);
      return state;

    case SHAREABLE.link_request:
      return R.merge(state, {
        loading: true,
      });

    case SHAREABLE.link_response:
      const link = action.data;
      return R.merge(state, {
        link,
        loading: false,
      });

    case SHAREABLE.link_error:
      return R.merge(state, {
        error: 'Error creating link.',
        loading: false,
      });

    case GET_BOOKMARKS.response:
      const { bookmarks } = action.data;
      return R.merge(state, {
        bookmarked_reports: bookmarks || [],
        loading: false,
        loadingStatus: LOADING_STATUS.LOADED,
        areBookmarkReportsLoaded: true,
      });

    case SAVE_BOOKMARK.response:
      const { bookmark } = action.data;
      return R.merge(state, {
        bookmarked_reports: [...state.bookmarked_reports, bookmark],
        loading: false,
        loadingStatus: LOADING_STATUS.LOADED,
      });

    case UPDATE_BOOKMARK.response:
      const { previousBookmark, updatedBookmark } = action.data;
      const { bookmarked_reports } = state;
      const updatedBookmarkIndex = state.bookmarked_reports.findIndex((b) => b.id === previousBookmark.id);
      return R.merge(state, {
        bookmarked_reports: [
          ...bookmarked_reports.slice(0, updatedBookmarkIndex),
          updatedBookmark,
          ...bookmarked_reports.slice(updatedBookmarkIndex + 1),
        ],
        loading: false,
        loadingStatus: LOADING_STATUS.LOADED,
      });

    case DELETE_BOOKMARK.response: {
      const { deletedBookmark } = action.data;

      return R.merge(state, {
        bookmarked_reports: state.bookmarked_reports.filter((b) => b.id !== deletedBookmark.id),
        loading: false,
        loadingStatus: LOADING_STATUS.LOADED,
      });
    }

    default:
      return state;
  }
}

const Shareable = reducerWrapper(INIT_STATE, Handler, {
  request: [GET_BOOKMARKS, SAVE_BOOKMARK, UPDATE_BOOKMARK, DELETE_BOOKMARK],
  error: [GET_BOOKMARKS, SAVE_BOOKMARK, UPDATE_BOOKMARK, DELETE_BOOKMARK],
});
export default Shareable;
